import { useState } from "react";
import {
  Keyboard,
  Lightbulb,
  Link2,
  ListTodo,
  LoaderCircle,
  Scale,
  Sparkles
} from "lucide-react";
import { makeId, saveRecord } from "../lib/db";
import { ingestSocialLink, smartCaptureThought } from "../lib/api";
import { socialPlatformLabel } from "../lib/links";
import { localDateKey, nowIso } from "../lib/time";
import type { Decision, Task, Thought } from "../types";
import { Button, Field, Modal } from "./ui";

type CaptureType = "thought" | "task" | "decision";

export function QuickCapture({
  onClose,
  onSaved,
  initialType = "thought"
}: {
  onClose: () => void;
  onSaved: (message: string) => void;
  initialType?: CaptureType;
}) {
  const [type, setType] = useState<CaptureType>(initialType);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [area, setArea] = useState("");
  const [decisionText, setDecisionText] = useState("");
  const [rationale, setRationale] = useState("");
  const [expectedOutcome, setExpectedOutcome] = useState("");
  const [reviewAt, setReviewAt] = useState("");
  const [error, setError] = useState("");
  const [smartSaving, setSmartSaving] = useState(false);

  const detectedPlatform =
    type === "thought" ? socialPlatformLabel(`${title}\n${body}`) : undefined;

  async function smartSave() {
    if (!body.trim()) {
      setError("先把链接或需要整理的内容放进输入框。");
      return;
    }
    setSmartSaving(true);
    setError("");
    const timestamp = nowIso();
    try {
      if (detectedPlatform) {
        const { source, organization } = await ingestSocialLink(
          `${title}\n${body}`.trim()
        );
        const thoughtIds: string[] = [];
        const proposals =
          organization.thoughts.length > 0
            ? organization.thoughts
            : [
                {
                  category: "other" as const,
                  title: organization.headline,
                  summary: organization.summary,
                  keyPoints: [],
                  facts: source.description,
                  judgment: "",
                  evidence: "",
                  nextStep: ""
                }
              ];
        for (const proposal of proposals) {
          const id = makeId();
          thoughtIds.push(id);
          await saveRecord<Thought>({
            id,
            kind: "thought",
            title: proposal.title,
            body: proposal.summary,
            createdAt: timestamp,
            updatedAt: timestamp,
            revision: 0,
            status: "open",
            category: proposal.category,
            facts: proposal.facts,
            judgment: proposal.judgment,
            evidence: proposal.evidence,
            nextStep: proposal.nextStep,
            reviewAt: proposal.reviewAt,
            keyPoints: proposal.keyPoints,
            transcript: [
              `页面标题：${source.title}`,
              `页面简介：${source.description}`,
              source.limitation
            ]
              .filter(Boolean)
              .join("\n\n"),
            sourceType: "link",
            sourceUrl: source.finalUrl,
            sourceTitle: source.title
          });
        }
        for (const proposal of organization.tasks) {
          const complete = Boolean(
            proposal.outcome?.trim() && proposal.nextAction?.trim()
          );
          await saveRecord<Task>({
            id: makeId(),
            kind: "task",
            title: proposal.title,
            body: "",
            createdAt: timestamp,
            updatedAt: timestamp,
            revision: 0,
            sourceIds: thoughtIds,
            status: complete ? "next" : "inbox",
            outcome: proposal.outcome ?? "",
            nextAction: proposal.nextAction ?? "",
            area: proposal.area ?? "",
            dueAt: proposal.dueAt,
            sourceType: "link",
            sourceUrl: source.finalUrl,
            sourceTitle: source.title,
            transcript: source.description
          });
        }
        onSaved(
          `已从${detectedPlatform}公开页面整理 ${proposals.length} 条思考、${organization.tasks.length} 项任务`
        );
      } else {
        if (body.trim().length < 20) {
          setError("内容太短，直接保存原文即可。");
          return;
        }
        const draft = await smartCaptureThought(body.trim());
        await saveRecord<Thought>({
          id: makeId(),
          kind: "thought",
          title: draft.title,
          body: draft.summary,
          createdAt: timestamp,
          updatedAt: timestamp,
          revision: 0,
          status: "open",
          category: draft.category,
          facts: draft.facts,
          judgment: draft.judgment,
          evidence: draft.evidence,
          nextStep: draft.nextStep,
          reviewAt: draft.reviewAt,
          keyPoints: draft.keyPoints,
          transcript: body.trim(),
          sourceType: "text"
        });
        onSaved(
          draft.warnings.length > 0
            ? `已自动拟题并保存；注意：${draft.warnings[0]}`
            : "已自动拟题、分类并保存"
        );
      }
      onClose();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "智能整理失败，原文还在输入框中。"
      );
    } finally {
      setSmartSaving(false);
    }
  }

  async function save() {
    setError("");
    const timestamp = nowIso();
    if (type === "thought") {
      if (!title.trim() && !body.trim()) {
        setError("先写下一句话，之后再慢慢整理。");
        return;
      }
      const thought: Thought = {
        id: makeId(),
        kind: "thought",
        title: title.trim() || body.trim().split(/\n/)[0].slice(0, 60),
        body: body.trim(),
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        status: "open",
        category: "other",
        facts: "",
        judgment: "",
        evidence: "",
        nextStep: "",
        sourceType: "manual"
      };
      await saveRecord(thought);
      onSaved("想法已收进工作台");
    } else if (type === "task") {
      if (!title.trim()) {
        setError("待办需要一句能看懂的任务标题。");
        return;
      }
      const task: Task = {
        id: makeId(),
        kind: "task",
        title: title.trim(),
        body: body.trim(),
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        status: "inbox",
        outcome: "",
        nextAction: "",
        area: area.trim()
      };
      await saveRecord(task);
      onSaved("待办已进入收件箱");
    } else {
      if (!title.trim() || !decisionText.trim() || !rationale.trim() || !expectedOutcome.trim() || !reviewAt) {
        setError("决策需要问题、决定、理由、预期结果和复核日期。");
        return;
      }
      const decision: Decision = {
        id: makeId(),
        kind: "decision",
        title: title.trim(),
        body: body.trim(),
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        status: "active",
        decision: decisionText.trim(),
        rationale: rationale.trim(),
        assumptions: "",
        alternatives: "",
        expectedOutcome: expectedOutcome.trim(),
        reviewAt
      };
      await saveRecord(decision);
      onSaved("决策已记录，并设置复核日期");
    }
    onClose();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      void save();
    }
  }

  return (
    <Modal
      title="记下一件事"
      eyebrow="随手记录"
      onClose={onClose}
      size={type === "decision" ? "large" : "medium"}
      footer={
        <>
          <span className="keyboard-hint">
            <Keyboard size={15} /> ⌘ Enter 保存
          </span>
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="button" variant="primary" onClick={() => void save()} disabled={smartSaving}>
            保存
          </Button>
        </>
      }
    >
      <div className="capture-layout" onKeyDown={onKeyDown}>
        <div className="capture-types" role="group" aria-label="记录类型">
          <button
            type="button"
            className={type === "thought" ? "capture-type active" : "capture-type"}
            onClick={() => setType("thought")}
            aria-pressed={type === "thought"}
          >
            <Lightbulb size={20} />
            <span>
              <strong>想法</strong>
              <small>先写下来，再整理</small>
            </span>
          </button>
          <button
            type="button"
            className={type === "task" ? "capture-type active" : "capture-type"}
            onClick={() => setType("task")}
            aria-pressed={type === "task"}
          >
            <ListTodo size={20} />
            <span>
              <strong>待办</strong>
              <small>只写标题也可以</small>
            </span>
          </button>
          <button
            type="button"
            className={type === "decision" ? "capture-type active" : "capture-type"}
            onClick={() => setType("decision")}
            aria-pressed={type === "decision"}
          >
            <Scale size={20} />
            <span>
              <strong>决策</strong>
              <small>记录当时为什么</small>
            </span>
          </button>
        </div>

        <div className="capture-form">
          <Field label={type === "decision" ? "要决定的问题" : "标题"} required={type !== "thought"}>
            <input
              className="input input-large"
              autoFocus
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={
                type === "thought"
                  ? "一句话也行"
                  : type === "task"
                    ? "例如：确认下周销售会主题"
                    : "例如：是否调整华东仓的低库存策略"
              }
            />
          </Field>

          {type === "task" && (
            <Field label="领域" hint="可稍后再补，例如销售、供应链、组织、财务。">
              <input
                className="input"
                value={area}
                onChange={(event) => setArea(event.target.value)}
                placeholder="销售 / 供应链 / 组织"
              />
            </Field>
          )}

          <Field label={type === "decision" ? "补充背景" : "内容"} hint={type === "thought" ? "手机可以直接用系统键盘听写。" : undefined}>
            <textarea
              className="textarea"
              rows={type === "thought" ? 7 : 4}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder={type === "thought" ? "事实、直觉、担心、机会，先不用分类……" : "需要保留的背景信息"}
            />
          </Field>

          {type === "thought" && body.trim().length >= 20 && (
            <div className="smart-capture-box">
              <div className="smart-capture-icon">
                {detectedPlatform ? <Link2 size={20} /> : <Sparkles size={20} />}
              </div>
              <div>
                <strong>
                  {detectedPlatform
                    ? `检测到${detectedPlatform}链接`
                    : "让 AI 自动拟题并归类"}
                </strong>
                <p>
                  {detectedPlatform
                    ? "尝试读取公开页面文字并整理。若平台拦截，会保留原文让你补充视频文案。"
                    : "从粘贴内容中生成标题、归属、摘要和关键点，整理完成后直接保存。"}
                </p>
              </div>
              <Button
                type="button"
                variant="primary"
                onClick={() => void smartSave()}
                disabled={smartSaving}
              >
                {smartSaving ? <LoaderCircle size={16} className="spin" /> : <Sparkles size={16} />}
                {smartSaving
                  ? "整理中…"
                  : detectedPlatform
                    ? "识别链接并保存"
                    : "AI 整理并保存"}
              </Button>
            </div>
          )}

          {type === "decision" && (
            <>
              <div className="form-grid">
                <Field label="决定" required>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={decisionText}
                    onChange={(event) => setDecisionText(event.target.value)}
                    placeholder="最终决定了什么？"
                  />
                </Field>
                <Field label="为什么这样定" required>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={rationale}
                    onChange={(event) => setRationale(event.target.value)}
                    placeholder="关键依据、取舍和反对意见"
                  />
                </Field>
              </div>
              <div className="form-grid">
                <Field label="预期结果" required>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={expectedOutcome}
                    onChange={(event) => setExpectedOutcome(event.target.value)}
                    placeholder="什么发生，才算这个决定有效？"
                  />
                </Field>
                <Field label="复核日期" required>
                  <input
                    className="input"
                    type="date"
                    min={localDateKey()}
                    value={reviewAt}
                    onChange={(event) => setReviewAt(event.target.value)}
                  />
                </Field>
              </div>
            </>
          )}
          {error && <p className="form-error">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}
